/**
 * Diagnostics — on-tablet diagnostic panel for the Leica bridge.
 *
 * Runs the exact search+lock+track RPC sequence against the connected
 * TS and shows the return code for every step. A "Copy report" button
 * dumps the whole thing as text so it can be shared with the developer
 * for further debugging.
 *
 * Why: this app runs against the surveyor's local bridge.py, not a
 * cloud endpoint the developer can hit. When something like Power
 * Search or Lock behaves oddly, the fastest diagnosis is: run the
 * scripted sequence here, share the RC report, pinpoint the failing
 * RPC exactly.
 */

import React, { useState } from "react";
import { Copy, Play, ArrowLeft, ScanSearch, CircleCheck, CircleX, Share2 } from "lucide-react";
import { toast } from "sonner";
import { getBackendUrl } from "../lib/api";
import { api } from "../lib/api";
import axios from "axios";

const RC_LABELS = {
  0: "OK",
  27: "GRC_NA — not licensed / not implemented on this firmware",
  257: "GRC_IVRESULT — invalid parameter",
  258: "GRC_ABORT — command aborted",
  1283: "GRC_TMC_NO_FULL_CORRECTION (warn — usable)",
  1284: "GRC_TMC_ACCURACY_GUARANTEE (warn — usable)",
  1285: "GRC_TMC_ANGLE_OK (no distance)",
  1292: "GRC_TMC_DIST_ERROR — no target",
  1459: "on-screen: PowerSearch working area not defined",
  1794: "GRC_MOT_UNREADY — motor controller not ready",
  8710: "GRC_AUT_NO_TARGET — search found nothing",
  8711: "GRC_AUT_MULTIPLE_TARGETS",
  8712: "GRC_AUT_BAD_ENVIRONMENT",
  8720: "GRC_AUT_NO_WORKING_AREA",
};

const rcHint = (rc) => RC_LABELS[rc] || (rc === undefined ? "(no reply)" : `rc=${rc}`);

const StatusPill = ({ ok, error }) => {
  if (error) {
    return (
      <span className="inline-flex items-center gap-1 rounded-sm border border-[#FF3B30]/60 bg-[#FF3B30]/10 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-[#FF3B30]">
        <CircleX size={10} strokeWidth={2.5} /> Error
      </span>
    );
  }
  return ok ? (
    <span className="inline-flex items-center gap-1 rounded-sm border border-[#8DC63F]/60 bg-[#8DC63F]/10 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-[#8DC63F]">
      <CircleCheck size={10} strokeWidth={2.5} /> OK
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-sm border border-[#FFB020]/60 bg-[#FFB020]/10 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-[#FFB020]">
      warn
    </span>
  );
};

export default function Diagnostics({ onBack }) {
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(null);   // "lock" | "probe" | "search" | null
  const [lockLog, setLockLog] = useState(null);   // array of RPC results
  const [probeLog, setProbeLog] = useState(null); // array
  const [searchLog, setSearchLog] = useState(null);   // { power_search_error, lock_reads, aux }

  const bridgeUrl = getBackendUrl();

  const runLockSequence = async () => {
    setBusy(true); setRunning("lock"); setLockLog(null);
    try {
      const r = await axios.get(`${bridgeUrl}/api/debug/lock-sequence`, { timeout: 60000 });
      setLockLog(r.data.sequence || []);
      toast.success("Lock sequence complete");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Sequence failed");
    } finally { setBusy(false); setRunning(null); }
  };

  const runSearchSequence = async () => {
    setBusy(true); setRunning("search"); setSearchLog(null);
    try {
      const r = await axios.get(`${bridgeUrl}/api/debug/search-sequence`, { timeout: 60000 });
      setSearchLog(r.data.sequence || null);
      if (r.data.sequence?.power_search_error) {
        toast.error(`PowerSearch: ${r.data.sequence.power_search_error}`);
      } else {
        toast.success("Search sequence complete");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Search failed");
    } finally { setBusy(false); setRunning(null); }
  };

  const [actionLog, setActionLog] = useState(null);
  const [mockLog, setMockLog] = useState(null);
  const [auditLog, setAuditLog] = useState(null);
  const runAudit = async () => {
    setBusy(true); setRunning("audit"); setAuditLog(null);
    try {
      const r = await axios.get(`${bridgeUrl}/api/debug/audit?limit=200`, { timeout: 10000 });
      setAuditLog(r.data || null);
      toast.success(`${r.data?.returned || 0} entries fetched`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Audit fetch failed");
    } finally { setBusy(false); setRunning(null); }
  };
  const runMockSearchLock = async () => {
    setBusy(true); setRunning("mock"); setMockLog(null);
    try {
      const r = await axios.get(`${bridgeUrl}/api/debug/mock-search-lock`, { timeout: 90000 });
      setMockLog(r.data || null);
      const verdict = r.data?.analysis?.verdict || "unknown";
      if (verdict === "locked_and_held") toast.success("Lock held for full 15 s monitor");
      else if (verdict === "never_locked") toast.error("Never engaged — check RPC events");
      else toast.warning(`Locked then dropped — ${verdict}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Mock failed");
    } finally { setBusy(false); setRunning(null); }
  };

  const runSearchAction = async () => {
    setBusy(true); setRunning("action"); setActionLog(null);
    try {
      const r = await axios.get(`${bridgeUrl}/api/debug/search-action`, { timeout: 60000 });
      setActionLog(r.data.sequence || null);
      if (r.data.sequence?.power_search_error) {
        toast.error(`PowerSearch: ${r.data.sequence.power_search_error}`);
      } else {
        toast.success("Search action + timeline complete");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Search action failed");
    } finally { setBusy(false); setRunning(null); }
  };

  const runFullProbe = async () => {
    setBusy(true); setRunning("probe"); setProbeLog(null);
    try {
      const r = await axios.get(`${bridgeUrl}/api/debug/probe`, { timeout: 90000 });
      setProbeLog(r.data.probe || []);
      toast.success("Firmware probe complete");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Probe failed");
    } finally { setBusy(false); setRunning(null); }
  };

  const formatReport = (log, title) => {
    const lines = [`=== ${title} ===`, `Bridge URL: ${bridgeUrl}`, `Time: ${new Date().toISOString()}`, ""];
    log.forEach((row) => {
      const rc = row.rc;
      const err = row.error;
      const vals = row.values ? ` values=[${row.values.join(", ")}]` : "";
      const status = err ? `ERROR ${err}` : `rc=${rc} ${RC_LABELS[rc] || ""}`;
      lines.push(`[${row.rpc}] ${row.label}  params="${row.params}"  →  ${status}${vals}`);
    });
    return lines.join("\n");
  };

  const copyReport = () => {
    const parts = [];
    if (lockLog) parts.push(formatReport(lockLog, "SEARCH + LOCK + TRACK SEQUENCE"));
    if (probeLog) parts.push(formatReport(probeLog, "FULL FIRMWARE PROBE"));
    if (parts.length === 0) { toast.error("Run a sequence first"); return; }
    const text = parts.join("\n\n");
    navigator.clipboard.writeText(text).then(
      () => toast.success("Report copied — paste it back to the developer"),
      () => toast.error("Clipboard write failed"),
    );
  };

  // Ship the current diagnostic JSON to the cloud backend so the
  // developer can read it directly without asking the surveyor to
  // copy-paste. Upserts a single "latest" record — one press, one
  // shared snapshot. Kept separate from the copy-to-clipboard path
  // (which stays useful for pasting into emails / GitHub issues).
  const [sharing, setSharing] = useState(false);
  const shareWithDev = async () => {
    if (!lockLog && !probeLog && !searchLog && !actionLog && !mockLog && !auditLog) { toast.error("Run a sequence first"); return; }
    setSharing(true);
    try {
      const payload = {
        bridgeUrl,
        timestamp: new Date().toISOString(),
        lockLog: lockLog || null,
        probeLog: probeLog || null,
        searchLog: searchLog || null,
        actionLog: actionLog || null,
        mockLog: mockLog || null,
        auditLog: auditLog || null,
        userAgent: navigator.userAgent,
      };
      const r = await api.post("/diagnostics/upload", payload);
      toast.success(`Shared with dev · ${r.data?.uploaded_at || "OK"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Upload failed");
    } finally { setSharing(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0B0E] px-4 py-4 text-white" data-testid="diagnostics-page">
      <div className="mx-auto max-w-4xl space-y-4">
        <div className="flex items-center justify-between">
          <button
            data-testid="diag-back-btn"
            onClick={onBack}
            className="flex items-center gap-1 rounded-md border border-[#2A2E39] bg-[#14161A] px-3 py-1.5 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#009E8E] hover:text-white"
          >
            <ArrowLeft size={12} /> Toolbox
          </button>
          <div className="flex items-center gap-2">
            <button
              data-testid="diag-share-btn"
              disabled={sharing || (!lockLog && !probeLog && !searchLog && !actionLog && !mockLog && !auditLog)}
              onClick={shareWithDev}
              className="flex items-center gap-1 rounded-md border border-[#F5A623]/60 bg-[#F5A623]/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#F5A623] hover:bg-[#F5A623]/20 disabled:opacity-40"
              style={{ fontFamily: "Chakra Petch, sans-serif" }}
            >
              <Share2 size={12} /> {sharing ? "Uploading…" : "Share with dev"}
            </button>
            <button
              data-testid="diag-copy-btn"
              disabled={!lockLog && !probeLog}
              onClick={copyReport}
              className="flex items-center gap-1 rounded-md border border-[#009E8E]/60 bg-[#009E8E]/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#009E8E] hover:bg-[#009E8E]/20 disabled:opacity-40"
              style={{ fontFamily: "Chakra Petch, sans-serif" }}
            >
              <Copy size={12} /> Copy report
            </button>
          </div>
        </div>

        <div className="rounded-md border border-[#2A2E39] bg-[#14161A] p-4">
          <div className="mb-2 flex items-center gap-2">
            <ScanSearch size={16} className="text-[#009E8E]" />
            <h2 className="text-sm font-bold uppercase tracking-widest text-white" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
              TS Diagnostics
            </h2>
          </div>
          <p className="text-xs text-[#8B939A]">
            Fires the exact search / lock / track sequence against the connected instrument and reports every RPC&apos;s return code.
            Use this to pinpoint which GeoCOM call your firmware refuses. Bridge: <span className="font-mono text-[#00E1C7]">{bridgeUrl}</span>
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <button
            data-testid="diag-run-lock-btn"
            disabled={busy}
            onClick={runLockSequence}
            className="flex flex-col items-start rounded-md border border-[#2A2E39] bg-[#14161A] p-4 text-left hover:border-[#009E8E] disabled:opacity-40"
          >
            <div className="mb-1 flex items-center gap-2">
              <Play size={14} className="text-[#009E8E]" />
              <span className="text-sm font-bold uppercase tracking-widest text-white" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
                Run 8-step lock sequence
              </span>
            </div>
            <span className="text-[10px] uppercase tracking-widest text-[#8B939A]">
              {running === "lock" ? "Running… make sure the crosshair is on the prism first" : "Aim at prism first · ATR → Lock → FineAdjust → LockIn → TRK → TMC_DoMeasure → 3× lock reads"}
            </span>
          </button>

          <button
            data-testid="diag-run-search-btn"
            disabled={busy}
            onClick={runSearchSequence}
            className="flex flex-col items-start rounded-md border border-[#F5A623]/60 bg-[#F5A623]/5 p-4 text-left hover:border-[#F5A623] disabled:opacity-40"
          >
            <div className="mb-1 flex items-center gap-2">
              <Play size={14} className="text-[#F5A623]" />
              <span className="text-sm font-bold uppercase tracking-widest text-white" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
                Run REAL Search+Lock (3 s)
              </span>
            </div>
            <span className="text-[10px] uppercase tracking-widest text-[#8B939A]">
              {running === "search" ? "Running the actual power_search() code path…" : "Runs gc.power_search() then polls MOT_ReadLockStatus every 300 ms for 3 s"}
            </span>
          </button>

          <button
            data-testid="diag-run-audit-btn"
            disabled={busy}
            onClick={runAudit}
            className="flex flex-col items-start rounded-md border-2 border-[#FF3B30] bg-[#FF3B30]/10 p-4 text-left hover:bg-[#FF3B30]/20 disabled:opacity-40 md:col-span-2"
          >
            <div className="mb-1 flex items-center gap-2">
              <Play size={14} className="text-[#FF3B30]" />
              <span className="text-sm font-bold uppercase tracking-widest text-white" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
                ⚑ FETCH AUDIT TRAIL — the "what did the tablet actually call?" log
              </span>
            </div>
            <span className="text-[10px] uppercase tracking-widest text-[#8B939A]">
              {running === "audit"
                ? "Fetching bridge audit log…"
                : "USE FOR THE LOCK BUG: right after Set Home & Lock Prism fails, tap this. Returns every HTTP call the tablet made against the bridge (with atr_locked/rtrk_armed/laser state before+after each call). This will DEFINITIVELY show which call is killing the lock."}
            </span>
          </button>

          <button
            data-testid="diag-run-mock-btn"
            disabled={busy}
            onClick={runMockSearchLock}
            className="flex flex-col items-start rounded-md border-2 border-[#00E1C7] bg-[#00E1C7]/5 p-4 text-left hover:bg-[#00E1C7]/10 disabled:opacity-40 md:col-span-2"
          >
            <div className="mb-1 flex items-center gap-2">
              <Play size={14} className="text-[#00E1C7]" />
              <span className="text-sm font-bold uppercase tracking-widest text-white" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
                MOCK SEARCH+LOCK (18 s) — the definitive test
              </span>
            </div>
            <span className="text-[10px] uppercase tracking-widest text-[#8B939A]">
              {running === "mock"
                ? "Firing engage RPCs one-by-one then monitoring MOT + USER_LOCK for 15 s…"
                : "Aim TS at prism first. Then reproduces production engage step-by-step, snapshots MOT + USER_LOCK + Hz/V between EVERY RPC, then monitors for 15 s. Verdict = never_locked / locked_and_held / locked_at_Xms_dropped_at_Yms"}
            </span>
          </button>

          <button
            data-testid="diag-run-action-btn"
            disabled={busy}
            onClick={runSearchAction}
            className="flex flex-col items-start rounded-md border border-[#FF3B30]/60 bg-[#FF3B30]/5 p-4 text-left hover:border-[#FF3B30] disabled:opacity-40"
          >
            <div className="mb-1 flex items-center gap-2">
              <Play size={14} className="text-[#FF3B30]" />
              <span className="text-sm font-bold uppercase tracking-widest text-white" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
                Search Action Timeline (5 s)
              </span>
            </div>
            <span className="text-[10px] uppercase tracking-widest text-[#8B939A]">
              {running === "action" ? "Firing action + logging state ↔ MOT for 5 s…" : "Reproduces the ACTUAL Search+Lock button flow. Shows state.atr_locked vs live MOT_ReadLockStatus at 4 Hz"}
            </span>
          </button>

          <button
            data-testid="diag-run-probe-btn"
            disabled={busy}
            onClick={runFullProbe}
            className="flex flex-col items-start rounded-md border border-[#2A2E39] bg-[#14161A] p-4 text-left hover:border-[#009E8E] disabled:opacity-40"
          >
            <div className="mb-1 flex items-center gap-2">
              <Play size={14} className="text-[#009E8E]" />
              <span className="text-sm font-bold uppercase tracking-widest text-white" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
                Full firmware probe
              </span>
            </div>
            <span className="text-[10px] uppercase tracking-widest text-[#8B939A]">
              {running === "probe" ? "Probing every known RPC…" : "Every RPC we know about — ~40 calls, ~30 s"}
            </span>
          </button>
        </div>

        {lockLog && (
          <RpcTable title="Search + Lock + Track" rows={lockLog} testid="lock-log" />
        )}
        {searchLog && (
          <div className="overflow-hidden rounded-md border border-[#F5A623]/60 bg-[#0F1116]" data-testid="search-log">
            <div className="border-b border-[#F5A623]/60 bg-[#14161A] px-3 py-2 text-[10px] uppercase tracking-widest text-[#F5A623]">
              Real power_search() result
            </div>
            <div className="p-3 text-[11px]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
              {searchLog.power_search_error ? (
                <div className="mb-3 rounded border border-[#FF3B30]/50 bg-[#FF3B30]/10 p-2 text-[#FF3B30]">
                  power_search error: {searchLog.power_search_error}
                </div>
              ) : (
                <div className="mb-3 text-[#8DC63F]">power_search returned OK</div>
              )}
              <div className="mb-2 text-[10px] uppercase tracking-widest text-[#8B939A]">Lock reads over 3 s:</div>
              <ul className="mb-3 space-y-1">
                {(searchLog.lock_reads || []).map((r, i) => (
                  <li key={i} className={r.values?.[0] === "1" ? "text-[#8DC63F]" : "text-[#8B939A]"}>
                    {r.label} → {r.values?.[0] ?? r.error ?? `rc=${r.rc}`}
                  </li>
                ))}
              </ul>
              <div className="mb-2 text-[10px] uppercase tracking-widest text-[#8B939A]">Aux reads:</div>
              <ul className="space-y-1">
                {(searchLog.aux || []).map((r, i) => (
                  <li key={i} className="text-[#B5BEC5]">
                    {r.label} → {r.values ? r.values.join(", ") : r.error ?? `rc=${r.rc}`}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
        {auditLog && (
          <div className="overflow-hidden rounded-md border-2 border-[#FF3B30] bg-[#0F1116]" data-testid="audit-log">
            <div className="border-b border-[#FF3B30] bg-[#14161A] px-3 py-2 text-[10px] uppercase tracking-widest text-[#FF3B30]">
              Bridge audit trail — every HTTP call made against the bridge, most recent last
              <span className="ml-2 text-[#8B939A]">bridge {auditLog.bridge_version || "unknown"}</span>
            </div>
            <div className="p-3 text-[10px]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
              <table className="w-full">
                <thead className="text-[9px] uppercase tracking-widest text-[#8B939A]">
                  <tr>
                    <th className="pr-2 text-left">t (ms)</th>
                    <th className="pr-2 text-left">method</th>
                    <th className="pr-2 text-left">path</th>
                    <th className="pr-2 text-left">query</th>
                    <th className="pr-2 text-left">status</th>
                    <th className="pr-2 text-left">dur</th>
                    <th className="pr-2 text-left">lock (before → after)</th>
                    <th className="pr-2 text-left">rtrk (b → a)</th>
                    <th className="pr-2 text-left">laser (b → a)</th>
                  </tr>
                </thead>
                <tbody>
                  {(auditLog.entries || []).map((e, i) => {
                    const b = e.before || {};
                    const a = e.after || {};
                    const lockFlip = b.atr_locked === true && a.atr_locked === false;
                    const rtrkFlip = b.rtrk_armed === true && a.rtrk_armed === false;
                    const cls = lockFlip || rtrkFlip ? "text-[#FF3B30] font-bold" : "text-[#B5BEC5]";
                    return (
                      <tr key={i} className={`border-b border-[#1A1D24] ${cls}`}>
                        <td className="pr-2">{e.t_ms}</td>
                        <td className="pr-2">{e.method}</td>
                        <td className="pr-2 max-w-xs truncate">{e.path}</td>
                        <td className="pr-2 max-w-xs truncate">{e.qs || "—"}</td>
                        <td className="pr-2">{e.status}</td>
                        <td className="pr-2">{e.duration_ms}ms</td>
                        <td className="pr-2">{String(b.atr_locked)}→{String(a.atr_locked)}</td>
                        <td className="pr-2">{String(b.rtrk_armed)}→{String(a.rtrk_armed)}</td>
                        <td className="pr-2">{String(b.laser_on)}→{String(a.laser_on)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="mt-3 text-[10px] text-[#8B939A]">
                Red bold rows = this HTTP call flipped atr_locked or rtrk_armed from true → false. That's the smoking gun.
              </div>
            </div>
          </div>
        )}
        {mockLog && (
          <div className="overflow-hidden rounded-md border-2 border-[#00E1C7] bg-[#0F1116]" data-testid="mock-log">
            <div className="border-b border-[#00E1C7] bg-[#14161A] px-3 py-2 text-[10px] uppercase tracking-widest text-[#00E1C7]">
              MOCK Search+Lock — step-by-step engage + 15 s monitor
            </div>
            <div className="p-3 text-[11px]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
              {mockLog.analysis && (
                <div className={`mb-3 rounded p-2 ${
                  mockLog.analysis.verdict === "locked_and_held" ? "bg-[#8DC63F]/10 text-[#8DC63F]" :
                  mockLog.analysis.verdict === "never_locked" ? "bg-[#FF3B30]/10 text-[#FF3B30]" :
                  "bg-[#F5A623]/10 text-[#F5A623]"
                }`}>
                  <div className="text-[10px] uppercase tracking-widest">Verdict</div>
                  <div className="text-lg font-bold">{mockLog.analysis.verdict}</div>
                  <div className="text-[10px]">
                    first_lock=<b>{String(mockLog.analysis.first_lock_at_ms)}</b>ms,
                    last_lock=<b>{String(mockLog.analysis.last_lock_at_ms)}</b>ms,
                    first_drop_after_lock=<b>{String(mockLog.analysis.first_drop_after_lock_ms)}</b>ms
                  </div>
                </div>
              )}
              <table className="w-full text-[10px]">
                <thead className="text-[9px] uppercase tracking-widest text-[#8B939A]">
                  <tr>
                    <th className="pr-2 text-left">t (ms)</th>
                    <th className="pr-2 text-left">event</th>
                    <th className="pr-2 text-left">label</th>
                    <th className="pr-2 text-left">rc / mot</th>
                    <th className="pr-2 text-left">user_lock</th>
                    <th className="pr-2 text-left">hz</th>
                    <th className="pr-2 text-left">v</th>
                    <th className="pr-2 text-left">dur</th>
                  </tr>
                </thead>
                <tbody>
                  {(mockLog.events || []).map((r, i) => {
                    const isSnap = r.event === "snapshot";
                    const isMot1 = isSnap && r.mot === 1;
                    const isMot0 = isSnap && r.mot === 0;
                    const isRpc = r.event === "rpc";
                    const rpcErr = isRpc && (r.error || (r.rc !== 0 && !(r.rc >= 1280 && r.rc <= 1299)));
                    const cls = isMot1 ? "text-[#8DC63F]" : isMot0 ? "text-[#FF3B30]" : rpcErr ? "text-[#F5A623]" : "text-[#B5BEC5]";
                    return (
                      <tr key={i} className={`border-b border-[#1A1D24] ${cls}`}>
                        <td className="pr-2">{r.t_ms}</td>
                        <td className="pr-2">{r.event}</td>
                        <td className="pr-2 max-w-xs truncate">{r.label}</td>
                        <td className="pr-2">
                          {isSnap
                            ? `mot=${r.mot}`
                            : isRpc
                            ? (r.error ? `ERR ${r.error}` : `rc=${r.rc}`)
                            : "—"}
                        </td>
                        <td className="pr-2">{isSnap ? String(r.user_lock) : "—"}</td>
                        <td className="pr-2">{isSnap && r.hz != null ? r.hz.toFixed(4) : "—"}</td>
                        <td className="pr-2">{isSnap && r.v != null ? r.v.toFixed(4) : "—"}</td>
                        <td className="pr-2">{isRpc ? `${r.duration_ms}ms` : ""}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="mt-3 text-[10px] text-[#8B939A]">{mockLog.note}</div>
            </div>
          </div>
        )}
        {actionLog && (
          <div className="overflow-hidden rounded-md border border-[#FF3B30]/60 bg-[#0F1116]" data-testid="action-log">
            <div className="border-b border-[#FF3B30]/60 bg-[#14161A] px-3 py-2 text-[10px] uppercase tracking-widest text-[#FF3B30]">
              Search action timeline
            </div>
            <div className="p-3 text-[11px]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
              {actionLog.power_search_error && (
                <div className="mb-3 rounded border border-[#FF3B30]/50 bg-[#FF3B30]/10 p-2 text-[#FF3B30]">
                  power_search error: {actionLog.power_search_error}
                </div>
              )}
              <table className="w-full text-[11px]">
                <thead className="text-[9px] uppercase tracking-widest text-[#8B939A]">
                  <tr>
                    <th className="text-left">t (ms)</th>
                    <th className="text-left">state.atr_locked</th>
                    <th className="text-left">MOT_ReadLockStatus</th>
                    <th className="text-left">AUS_user_lock</th>
                    <th className="text-left">unlocked_streak</th>
                  </tr>
                </thead>
                <tbody>
                  {(actionLog.timeline || []).map((r, i) => (
                    <tr key={i} className={r.state_atr_locked ? "text-[#8DC63F]" : "text-[#FF3B30]"}>
                      <td className="pr-2">{r.t_ms}</td>
                      <td className="pr-2">{String(r.state_atr_locked)}</td>
                      <td className="pr-2">{String(r.mot_lock_status)}</td>
                      <td className="pr-2">{String(r.aus_user_lock)}</td>
                      <td className="pr-2">{r.unlocked_streak}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {probeLog && (
          <RpcTable title="Full Firmware Probe" rows={probeLog} testid="probe-log" />
        )}
      </div>
    </div>
  );
}

const RpcTable = ({ title, rows, testid }) => (
  <div className="overflow-hidden rounded-md border border-[#2A2E39] bg-[#0F1116]" data-testid={testid}>
    <div className="border-b border-[#2A2E39] bg-[#14161A] px-3 py-2 text-[10px] uppercase tracking-widest text-[#8B939A]">
      {title}
    </div>
    <div className="max-h-[60vh] overflow-y-auto">
      <table className="w-full text-[11px]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
        <thead className="sticky top-0 border-b border-[#2A2E39] bg-[#14161A] text-[9px] uppercase tracking-widest text-[#4A5054]">
          <tr>
            <th className="px-2 py-1 text-left">RPC</th>
            <th className="px-2 py-1 text-left">Label</th>
            <th className="px-2 py-1 text-left">Params</th>
            <th className="px-2 py-1 text-left">Status</th>
            <th className="px-2 py-1 text-left">Detail</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            const ok = row.rc === 0;
            const warn = row.rc >= 1280 && row.rc <= 1299;
            return (
              <tr key={idx} className="border-b border-[#1A1D24]">
                <td className="px-2 py-1 text-[#00E1C7]">{row.rpc}</td>
                <td className="px-2 py-1 text-white">{row.label}</td>
                <td className="px-2 py-1 text-[#8B939A]">{row.params || "—"}</td>
                <td className="px-2 py-1">
                  <StatusPill ok={ok || warn} error={row.error} />
                </td>
                <td className="px-2 py-1 text-[#8B939A]">
                  {row.error
                    ? row.error
                    : `rc=${row.rc}${row.rc !== 0 ? ` ${rcHint(row.rc)}` : ""}${
                        row.values && row.values.length ? `  →  ${row.values.join(", ")}` : ""
                      }`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);
